import React, {useEffect} from 'react';

import {
  FlatList,
  View,
  ScrollView,
  TouchableOpacity,
  Image,
} from 'react-native';

import TrustGrid from '../components/TrustContent/TrustGrid';
import MaxWidthContainer from '../components/MaxWidthContainer';
import TrustFeaturedCard from '../components/TrustContent/TrustFeaturedCard';
import ImageNavCardLayoutHeader from '../components/ImageNavCard/ImageNavCardLayoutHeader';
import DataController from '../classes/DataController';
import {colors} from '../classes/Colors';
import {getTrustBlocks, getQuizzesForTrust} from '../utils/trustContent';
import {CONTENT_TYPES} from '../constants/trustContent';
import TrustStatus from '../components/TrustContent/TrustStatus';
import {logAnalyticsEvent} from '../utils/firebase';
import {EVENT_NAMES, SCREEN_NAMES} from '../constants/events';
import {orderBy, union} from 'lodash';

const getDateForType = (type, item) => {
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      const topSection = item?.content.find(
        content => content.type === 'TopSection',
      );
      return new Date(topSection?.content?.DisplayDate);
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return new Date(item.PublicDate);
    }
    case CONTENT_TYPES.PODCASTS: {
      return new Date(item.ReleaseDate);
    }
    case CONTENT_TYPES.VIDEOS: {
      return new Date(item.ReleaseDate);
    }
    case CONTENT_TYPES.QUIZZES: {
      return new Date(item.date);
    }
    case CONTENT_TYPES.PGDS: {
      return new Date(item.date);
    }
    case CONTENT_TYPES.PMAS: {
      return new Date(item.date);
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return new Date(item.PublicDate);
    }
    default:
      return null;
  }
};

const getTitleForType = (type, item) => {
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      return item?.Name;
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return item.Title;
    }
    case CONTENT_TYPES.PODCASTS: {
      return item.Title;
    }
    case CONTENT_TYPES.VIDEOS: {
      return item.Title;
    }
    case CONTENT_TYPES.QUIZZES: {
      return item.title;
    }
    case CONTENT_TYPES.PGDS: {
      return item.title;
    }
    case CONTENT_TYPES.PMAS: {
      return item.title;
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return item.Title;
    }
    default:
      return null;
  }
};

const getSubTitleForType = (type, item) => {
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      return item.DisplayTitle;
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return item.Author[0];
    }
    case CONTENT_TYPES.PODCASTS: {
      return item.ShortDescription;
    }
    case CONTENT_TYPES.VIDEOS: {
      return item.ShortDescription;
    }
    case CONTENT_TYPES.QUIZZES: {
      return item.section;
    }
    case CONTENT_TYPES.PGDS: {
      return item.title;
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return item.Author[0];
    }
    default:
      return null;
  }
};

const getImageForType = (type, item) => {
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      const topSection = item?.content.find(
        content => content.type === 'TopSection',
      );
      return () => topSection?.content.TopImage[0].url;
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return () =>
        DataController.getStandbyIssueImagePath(
          item,
          item?.CoverImage?.[0]?.filename,
        );
    }
    case CONTENT_TYPES.PODCASTS: {
      return () => item.imageUrl;
    }
    case CONTENT_TYPES.VIDEOS: {
      return () =>
        `https://ppp-temp-files.s3.amazonaws.com/VideoPodcasts/${item.Screenshot[0].filename}`;
    }
    case CONTENT_TYPES.QUIZZES: {
      return () => null;
    }
    case CONTENT_TYPES.PGDS: {
      return () => null;
    }
    case CONTENT_TYPES.PMAS: {
      return () => null;
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return () =>
        DataController.getStandbyIssueImagePath(
          item,
          item?.CoverImage?.[0]?.filename,
        );
    }
    default:
      return () => null;
  }
};

const getIdForType = (type, item, index) => {
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      return `${item.ID}-${index}`;
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return `${item.Name}-${index}`;
    }
    case CONTENT_TYPES.PODCASTS: {
      return `${item.id}-${index}`;
    }
    case CONTENT_TYPES.VIDEOS: {
      return `${item.id}-${index}`;
    }
    case CONTENT_TYPES.QUIZZES: {
      return `${item.name}-${index}`;
    }
    case CONTENT_TYPES.PGDS: {
      return `${item.title}-${index}`;
    }
    case CONTENT_TYPES.PMAS: {
      return `${item.title}-${index}`;
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return `${item.Name}-${index}`;
    }
    default:
      return null;
  }
};

const getOnPressForType = (type, item) => {
  const screen = 'Trust Hub';
  switch (type) {
    case CONTENT_TYPES.PSIRF: {
      return navigation => {
        navigation.navigate('PSIRF', {data: item, from: screen});
      };
    }
    case CONTENT_TYPES.CASE_STUDIES: {
      return (navigation, screenProps) => {
        const quizSet = screenProps?.quizSets?.find(
          quizSet => quizSet.issueName === item.Name,
        );
        navigation.navigate('CaseStudyOverview', {
          caseStudy: item,
          quizSet,
          from: screen,
        });
      };
    }
    case CONTENT_TYPES.PODCASTS: {
      return (navigation, screenProps) => {
        const audioHistory = screenProps.audioHistory;
        const updateAudioHistory = screenProps.updateAudioHistory;

        return navigation.navigate('Podcast', {
          podcast: item,
          audioHistory,
          updateAudioHistory,
        });
      };
    }
    case CONTENT_TYPES.VIDEOS: {
      return navigation => {
        navigation.navigate('Video', {video: item, from: screen});
      };
    }
    case CONTENT_TYPES.QUIZZES: {
      return navigation => {
        navigation.navigate('QuizOverview', {
          quizSet: item,
          from: screen,
        });
      };
    }
    case CONTENT_TYPES.PGDS: {
      return navigation => {
        navigation.navigate('QuizOverview', {quizSet: item, from: screen});
      };
    }
    case CONTENT_TYPES.PMAS: {
      return navigation => {
        navigation.navigate('QuizOverview', {quizSet: item, from: screen});
      };
    }
    case CONTENT_TYPES.STANDBY_CPD: {
      return (navigation, screenProps) => {
        const quizSet = screenProps?.quizSets?.find(
          quizSet => quizSet.issueName === item.Name,
        );
        return navigation.navigate('StandbyVolume', {
          issue: item,
          quizSet,
        });
      };
    }
    default: {
      return null;
    }
  }
};

const getDisplayTypeForItem = (type, item) => {
  if (type === CONTENT_TYPES.PSIRF) {
    // if psirf, then use the type of the from the psirf
    return item?.Type || type;
  }

  return type;
};

const normalizeContent = (...args) => {
  return args
    .filter(arg => arg?.data?.length)
    .map(({data, type}, index) => {
      return data.map(item => {
        return {
          item,
          type,
          displayType: getDisplayTypeForItem(type, item),
          id: getIdForType(type, item, index),
          date: getDateForType(type, item),
          title: getTitleForType(type, item),
          subtitle: getSubTitleForType(type, item),
          getImage: getImageForType(type, item),
          onPress: getOnPressForType(type, item),
        };
      });
    });
};

const orderDataByDate = data => {
  return data.sort((a, b) => b.date - a.date);
};

const TrustDashboardScreen = ({navigation, screenProps}) => {
  const userTrusts = union(
    screenProps?.user?.paraPassTrusts,
    screenProps?.user?.plusTrusts,
  );
  const trust = userTrusts[0];
  const from = navigation.getParam('from', 'unknown');

  useEffect(() => {
    try {
      logAnalyticsEvent(EVENT_NAMES.screen, {
        screen_name: SCREEN_NAMES.trustDashboard,
        from,
        trust,
      });
    } catch (error) {
      console.log(error);
    }
  }, [from, trust]);

  if (!trust) {
    return null;
  }

  const psirfs = screenProps.psirfs.filter(p => p.Trust === trust);
  const caseStudies = screenProps.caseStudies.filter(c => c.Trust === trust);
  const podcasts = screenProps.podcasts.filter(p => p.Trust === trust);
  const videos = screenProps.videos.filter(v => v.Trust === trust);
  const quizzes = getQuizzesForTrust(
    screenProps.quizSets,
    trust,
    screenProps.topics,
  );
  const pgds = screenProps.quizSets
    .filter(q => q.pgd === true && q.pma !== true)
    .filter(q => q.trust === trust);
  const pmas = screenProps.quizSets
    .filter(q => q.pgd === true && q.pma === true)
    .filter(q => q.trust === trust);
  const issues = screenProps.issues.filter(i => i.Trust === trust);

  const content = normalizeContent(
    {data: psirfs, type: CONTENT_TYPES.PSIRF},
    {data: caseStudies, type: CONTENT_TYPES.CASE_STUDIES},
    {data: podcasts, type: CONTENT_TYPES.PODCASTS},
    {data: videos, type: CONTENT_TYPES.VIDEOS},
    {data: quizzes, type: CONTENT_TYPES.QUIZZES},
    {data: pgds, type: CONTENT_TYPES.PGDS},
    {data: pmas, type: CONTENT_TYPES.PMAS},
    {data: issues, type: CONTENT_TYPES.STANDBY_CPD},
  );
  const orderedData = orderDataByDate(content.flat()).slice(0, 5);

  const trustBlocks = getTrustBlocks(screenProps, navigation, false);
  const pinSectionBlocks = orderBy(
    trustBlocks.filter(block => block.trustHubSection === 'Pins'),
    ['order'],
    ['asc'],
  );

  const librarySection = orderBy(
    trustBlocks.filter(block => block.trustHubSection === 'Content Library'),
    ['title'],
    ['asc'],
  );

  const renderItem = ({item, index}) => {
    const isLast = index === orderedData.length - 1;
    const statusComponent = (
      <TrustStatus
        type={item.type}
        item={item.item}
        screenProps={screenProps}
      />
    );
    return (
      <View style={[{flex: 1, marginRight: 16}, isLast && {marginRight: 0}]}>
        <TrustFeaturedCard
          {...item}
          onPress={() => item.onPress(navigation, screenProps)}
          statusComponent={statusComponent}
        />
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={{backgroundColor: colors.white}}>
      <MaxWidthContainer contentContainerStyle={{flex: 1}}>
        {Boolean(orderedData.length) && (
          <View>
            <ImageNavCardLayoutHeader title="Latest from" trust={trust} />
            <FlatList
              data={orderedData}
              renderItem={renderItem}
              keyExtractor={item => item.id}
              contentContainerStyle={{paddingBottom: 16, minHeight: 318}}
              horizontal
              snapToAlignment="start"
              decelerationRate="fast"
              snapToEnd={false}
              disableIntervalMomentum
              snapToInterval={320}
            />
          </View>
        )}
        {Boolean(pinSectionBlocks.length) && (
          <View style={{paddingTop: 16}}>
            <ImageNavCardLayoutHeader trust={trust} secondTitle="Pins" />
            <TrustGrid
              trustBlocks={pinSectionBlocks}
              screenProps={screenProps}
              navigation={navigation}
            />
          </View>
        )}
        {Boolean(librarySection.length) && (
          <View style={{paddingTop: 16}}>
            <ImageNavCardLayoutHeader
              trust={trust}
              secondTitle="Content Library"
            />
            <TrustGrid
              trustBlocks={librarySection}
              screenProps={screenProps}
              navigation={navigation}
            />
          </View>
        )}
      </MaxWidthContainer>
    </ScrollView>
  );
};

TrustDashboardScreen.navigationOptions = ({navigation}) => {
  const searchButtonOnPress = navigation.getParam('searchButtonOnPress');
  const rightButtonsLayout = (
    <TouchableOpacity onPress={searchButtonOnPress}>
      <Image
        style={{marginRight: 10, width: 44, height: 44}}
        source={require('../assets/Button_Search.png')}
      />
    </TouchableOpacity>
  );

  return {
    title: '',
    headerStyle: navigation.getParam('headerStyle', {
      backgroundColor: colors.NavBar,
    }),
    headerTintColor: navigation.getParam('headerTintColor', colors.white),
    headerTitleStyle: navigation.getParam('headerTitleStyle', {
      fontFamily: 'InterUI-Regular',
      fontSize: 17,
      fontWeight: '600',
      letterSpacing: -0.41,
      color: colors.white,
    }),
    headerRight: rightButtonsLayout,
  };
};

export default TrustDashboardScreen;
